/**
 * Detalhe completo de EMPRESA beneficiada PE — Brasil Transparente.
 *
 * Rota: /brasil-transparente/empresa/:cnpj
 * Fontes: emendas_raw (Tesouro) + BrasilAPI (CNPJ/QSA).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import {
  ArrowLeft, AlertCircle, Share2, ExternalLink, Building2, User,
  ChevronDown, ChevronRight, Star, MapPin, Info, CheckCircle2, XCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const YEAR_ELEITORAL = 2026;

const brl = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';
const num = (v) => (typeof v === 'number' ? v.toLocaleString('pt-BR') : '—');
const cnpjFmt = (c) => {
  const s = (c || '').replace(/\D/g, '');
  if (s.length !== 14) return c || '—';
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12, 14)}`;
};
const dateFmt = (s) => {
  if (!s) return '—';
  const parts = s.split('-');
  if (parts.length !== 3) return s;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const SEVERITY = {
  high: { cls: 'bg-red-500/10 border-red-500/40 text-red-200', icon: XCircle },
  medium: { cls: 'bg-amber-500/10 border-amber-500/40 text-amber-200', icon: AlertCircle },
  info: { cls: 'bg-blue-500/10 border-blue-500/40 text-blue-200', icon: Info },
};

export default function BrasilTransparenteEmpresa() {
  const { cnpj } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(
          `${API}/public/brasil-transparente/PE/empresa/${cnpj}`,
          { timeout: 30000 },
        );
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [cnpj]);

  const empresa = data?.cnpj_dados;
  const emendas = data?.emendas;
  const alertas = data?.alertas || [];
  const shareUrl = `${BACKEND_URL}/api/public/brasil-transparente/PE/share/empresa/${cnpj}.png`;
  const portalUrl = `https://portaldatransparencia.gov.br/pessoa-juridica/${cnpj}`;
  const receitaUrl = `https://www.receita.fazenda.gov.br/pessoajuridica/cnpj/cnpjreva/cnpjreva_solicitacao.asp`;

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24" data-testid="bt-empresa-page">
      <header className="border-b border-neutral-800 bg-gradient-to-br from-emerald-900/40 via-neutral-950 to-neutral-950 px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/brasil-transparente"
            className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-emerald-400"
            data-testid="back-to-bt"
          >
            <ArrowLeft size={14} /> Brasil Transparente
          </Link>
          <div className="mt-3 flex items-center gap-2 mb-1">
            <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-mono">
              Empresa beneficiada · PE
            </span>
          </div>
          {loading ? (
            <div className="h-10 mt-3 bg-neutral-900 rounded animate-pulse" />
          ) : (
            <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight mt-1 break-words">
              {empresa?.razao_social || emendas?.nome_favorecido || 'Empresa sem cadastro'}
            </h1>
          )}
          <div className="mt-2 text-xs text-neutral-400 font-mono">
            CNPJ: {cnpjFmt(cnpj)}
          </div>

          {!loading && (
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="share-empresa-png"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[11px] hover:bg-emerald-500/20"
              ><Share2 size={12} /> Card WhatsApp</a>
              <a
                href={portalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300 hover:border-emerald-500"
              ><ExternalLink size={12} /> Portal da Transparência</a>
              <a
                href={receitaUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300 hover:border-emerald-500"
              ><ExternalLink size={12} /> Consultar na Receita</a>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {loading && <Skeleton />}
        {!loading && err && <ErrorBox />}

        {/* Alertas topo */}
        {!loading && alertas.length > 0 && (
          <div className="space-y-2" data-testid="alertas-list">
            {alertas.map((a, i) => {
              const S = SEVERITY[a.severity] || SEVERITY.info;
              const Ic = S.icon;
              return (
                <Card
                  key={a.code + i}
                  className={`${S.cls} p-3 flex gap-2 text-sm`}
                  data-testid={`alerta-${a.code}`}
                >
                  <Ic size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{a.titulo}</div>
                    {a.detalhe && <div className="text-xs opacity-90 mt-0.5">{a.detalhe}</div>}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Identificação */}
        {!loading && empresa && empresa.ok && (
          <Section title="Identificação" testid="section-ident">
            <Card className="bg-neutral-900 border-neutral-800 p-4 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {empresa.ativa ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[11px]">
                    <CheckCircle2 size={11} /> ATIVA
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/40 text-red-300 text-[11px]">
                    <XCircle size={11} /> {empresa.situacao_cadastral}
                  </span>
                )}
                {empresa.nome_fantasia && (
                  <span className="text-[11px] text-neutral-400">
                    Nome fantasia: {empresa.nome_fantasia}
                  </span>
                )}
              </div>
              <Field label="CNAE Principal" value={empresa.cnae_principal || '—'} />
              <Field label="Início de atividade" value={dateFmt(empresa.data_inicio_atividade)} />
              <Field
                label="Capital social"
                value={typeof empresa.capital_social === 'number'
                  ? brl(empresa.capital_social) : '—'}
              />
              <Field
                label="Endereço"
                value={
                  <>
                    {empresa.endereco?.logradouro} {empresa.endereco?.numero}<br />
                    {empresa.endereco?.bairro} · {empresa.municipio}/{empresa.uf}<br />
                    <span className="font-mono text-[11px] text-neutral-500">
                      CEP {empresa.endereco?.cep}
                    </span>
                  </>
                }
              />
              <div className="text-[10px] text-neutral-500 pt-1">
                Fonte: {empresa.fonte}
              </div>
            </Card>
          </Section>
        )}

        {!loading && empresa && !empresa.ok && (
          <Card className="bg-neutral-900 border-neutral-800 p-4 text-sm text-neutral-400" data-testid="ident-none">
            <div className="text-neutral-300 font-semibold mb-1">Sem cadastro na BrasilAPI</div>
            {empresa.error || 'Este CNPJ não foi encontrado na base pública. Pode ser um órgão público federal, uma instituição sem registro na Receita ou um CNPJ inválido.'}
          </Card>
        )}

        {/* Sócios */}
        {!loading && empresa?.ok && (empresa.socios || []).length > 0 && (
          <Section
            title="Sócios e administradores"
            subtitle="Fonte: BrasilAPI (QSA da Receita Federal)"
            testid="section-socios"
          >
            <div className="space-y-2">
              {empresa.socios.map((s, i) => (
                <Card
                  key={s.nome + i}
                  className="bg-neutral-900 border-neutral-800 p-3 flex gap-3"
                  data-testid={`socio-${i}`}
                >
                  <User size={16} className="text-neutral-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white break-words">
                      {s.nome}
                    </div>
                    <div className="text-[11px] text-neutral-400 mt-0.5">
                      {s.qualificacao}
                      {s.faixa_etaria && <> · faixa etária {s.faixa_etaria}</>}
                    </div>
                    {s.data_entrada && (
                      <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                        Membro desde {dateFmt(s.data_entrada)}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </Section>
        )}

        {/* Emendas recebidas */}
        {!loading && emendas?.ok && emendas.total > 0 && (
          <Section
            title="Emendas recebidas"
            subtitle="Fonte: Portal da Transparência / Tesouro Nacional"
            testid="section-emendas"
          >
            <Card className="bg-neutral-900 border-neutral-800 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
                Total recebido
              </div>
              <div className="text-2xl sm:text-3xl font-black text-emerald-400 mt-1">
                {brl(emendas.total)}
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                {num(emendas.count)} pagamentos · {(emendas.por_municipio || []).length} municípios
              </div>
            </Card>

            {/* Por ano — expandíveis */}
            <div className="space-y-2 mt-3">
              {(emendas.por_ano || []).map((y) => (
                <YearAccordion
                  key={y.ano}
                  ano={y.ano}
                  total={y.total}
                  isEleitoral={y.ano === YEAR_ELEITORAL}
                />
              ))}
            </div>

            {/* Por município */}
            <h3 className="text-xs uppercase tracking-widest text-neutral-500 font-mono mt-4 mb-2">
              Distribuição por município
            </h3>
            <div className="space-y-1.5">
              {(emendas.por_municipio || []).map((m, i) => (
                <Link
                  key={m.municipio + i}
                  to={`/brasil-transparente/municipio/${encodeURIComponent(m.municipio)}`}
                  data-testid={`emp-muni-${i}`}
                  className="block"
                >
                  <div className="flex items-center gap-2 px-3 py-2 rounded bg-neutral-900 border border-neutral-800 hover:border-emerald-500 transition">
                    <MapPin size={13} className="text-neutral-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{m.municipio}</div>
                      <div className="text-[10px] text-neutral-500">
                        {num(m.count)} pagamentos
                      </div>
                    </div>
                    <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
                      {brl(m.total)}
                    </div>
                    <ChevronRight size={14} className="text-neutral-500 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {!loading && emendas && emendas.total === 0 && (
          <Card className="bg-neutral-900 border-neutral-800 p-4 text-sm text-neutral-400">
            Nenhuma emenda registrada para este CNPJ no CSV atual do Tesouro.
          </Card>
        )}
      </main>
    </div>
  );
}

/* ── UI helpers ─────────────────────────────────────────── */
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{label}</div>
      <div className="text-sm text-white mt-0.5 break-words">{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-sm font-semibold text-neutral-200">{title}</h2>
      {subtitle && <p className="text-[11px] text-neutral-500 mb-2 mt-0.5">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function YearAccordion({ ano, total, isEleitoral }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded overflow-hidden border ${
        isEleitoral ? 'bg-amber-500/5 border-amber-500/30' : 'bg-neutral-900 border-neutral-800'
      }`}
      data-testid={`ano-accordion-${ano}`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        {open ? <ChevronDown size={14} className="text-neutral-500" /> : <ChevronRight size={14} className="text-neutral-500" />}
        <span className="text-sm text-white flex items-center gap-2">
          {ano}
          {isEleitoral && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-mono uppercase">
              <Star size={9} /> Eleitoral
            </span>
          )}
        </span>
        <div className="flex-1" />
        <span className={`text-sm font-bold font-mono ${isEleitoral ? 'text-amber-300' : 'text-emerald-400'}`}>
          {brl(total)}
        </span>
      </button>
      {open && (
        <div className="px-3 py-2 border-t border-neutral-800 text-[11px] text-neutral-500">
          O detalhe individual de cada pagamento (mês, ordem bancária, tipo)
          está disponível na aba <b>Emendas</b> do Brasil Transparente filtrada
          por ano e município.
        </div>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="bt-empresa-skeleton">
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-32 rounded bg-neutral-900 border border-neutral-800" />
      <div className="h-24 rounded bg-neutral-900 border border-neutral-800" />
    </div>
  );
}

function ErrorBox() {
  return (
    <Card className="bg-red-950/30 border-red-900 p-4 text-sm text-red-200 flex gap-2" data-testid="bt-empresa-error">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <div>Não foi possível carregar os dados desta empresa.</div>
    </Card>
  );
}
